'use client'

import React, { useMemo, useState, useEffect } from 'react'
import { FilterDropdown, FilterOption } from '@/shared/components/FilterDropdown'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/shared/lib/api'
import useGetMasterTypes from '@/shared/hooks/categories/masterTypes/useGet'
import useGetMasterTypeCatalog from '@/shared/hooks/categories/masterTypes/useGetMasterTypeDetail'
import { useGetUsers } from '@/shared/hooks/users/useGet'
import { MASTER_TYPE_SLUG } from '@/shared/constants/master-types'
import { User, MapPin, FolderOpen, FileText, CheckCircle } from 'lucide-react'
import { BsDateRangePicker, BsDateRangePickerValue } from '@workspace/ui/components/DatePicker'
import { useDateRangeQuickOptions } from '@/shared/hooks/useDateRangeQuickOptions'
import dayjs from 'dayjs'

export interface FilterProps {
    className?: string
    onFilterChange: (filters: Record<string, string[]>) => void
    projectId?: string
}

export function Filter({ className, onFilterChange, projectId }: FilterProps) {
    const { t } = useTranslation()
    const [filters, setFilters] = useState<Record<string, string[]>>({})
    const [dateRange, setDateRange] = useState<BsDateRangePickerValue | undefined>(undefined)

    const { data: masterTypesData } = useGetMasterTypes()
    const masterTypeMap = useMemo(() => {
        const filtered = masterTypesData?.data?.find(item => MASTER_TYPE_SLUG.DOCUMENT_TYPE.includes(item.code))
        return filtered?.id
    }, [masterTypesData])

    // 2. Fetch Document Types
    const { data: documentTypeOptions, isLoading: isDocumentTypeLoading } = useGetMasterTypeCatalog({
        masterTypeId: masterTypeMap,
        pageSize: 50,
    })

    // 3. Fetch Users (Person in Charge)
    const { data: usersOptions, isLoading: isUsersLoading } = useGetUsers({
        pageSize: 100,
        enabled: true,
        meta: { skipGlobalErrorHandler: true },
    })

    // 4. Fetch Regions
    const { data: regionsData, isLoading: isRegionsLoading } = useQuery({
        queryKey: ['regions'],
        queryFn: () => api.region.list({ page: 1, take: 100 }),
    })

    const { data: areasData, isLoading: isAreasLoading } = useQuery({
        queryKey: ['areas'],
        queryFn: () => api.area.list({ page: 1, take: 100 }),
    })

    // 6. Fetch Projects
    const { data: projectsData, isLoading: isProjectsLoading } = useQuery({
        queryKey: ['projects'],
        queryFn: () => api.project.list({ page: 1, take: 100 }),
    })

    // 7. Status Options
    const statusOptions = useMemo(
        () => [
            { id: 'PREPARING', name: t('projects.list.status.preparation') },
            { id: 'IN_PROGRESS', name: t('projects.list.status.inProgress') },
            { id: 'COMPLETED', name: t('projects.list.status.completed') },
            { id: 'ON_HOLD', name: t('projects.list.status.onHold') },
            { id: 'CANCELLED', name: t('projects.list.status.cancelled') },

            { id: 'todo', name: t('globalSearch.taskStatus.todo') },
            { id: 'receiving', name: t('globalSearch.taskStatus.receiving') },
            { id: 'in_progress', name: t('globalSearch.taskStatus.in_progress') },
            { id: 'paused', name: t('globalSearch.taskStatus.paused') },
            { id: 'waiting_approval', name: t('globalSearch.taskStatus.waiting_approval') },
            { id: 'completed', name: t('globalSearch.taskStatus.completed') },
            { id: 'cancelled', name: t('globalSearch.taskStatus.cancelled') },
            { id: 'overdue', name: t('globalSearch.taskStatus.overdue') },
        ],
        [t],
    )

    // Data Transformation to FilterOptions
    const docTypeOpts: FilterOption[] = useMemo(
        () => documentTypeOptions?.data?.map(d => ({ id: d.id, name: d.name })) || [],
        [documentTypeOptions],
    )

    const regionOpts: FilterOption[] = useMemo(
        () => regionsData?.data?.map(r => ({ id: r.id, name: r.name })) || [],
        [regionsData],
    )

    const areaOpts: FilterOption[] = useMemo(() => {
        const allAreas = areasData?.data || []
        const selectedRegions = filters['region'] || []
        if (selectedRegions.length === 0) return []
        return allAreas
            .filter((a: any) => selectedRegions.includes(a.region?.id))
            .map((a: any) => ({ id: a.id, name: a.name }))
    }, [areasData, filters.region])

    const projectOpts: FilterOption[] = useMemo(() => {
        const allProjects = projectsData?.data || []
        const selectedRegions = filters['region'] || []
        const selectedAreas = filters['area'] || []
        return allProjects
            .filter((p: any) => {
                const matchRegion = selectedRegions.length === 0 || (p.region && selectedRegions.includes(p.region.id))
                const matchArea = selectedAreas.length === 0 || (p.area && selectedAreas.includes(p.area.id))
                return matchRegion && matchArea
            })
            .map((p: any) => ({ id: p.id, name: p.name }))
    }, [projectsData, filters.region, filters.area])

    const userOpts: FilterOption[] = useMemo(() => {
        const allUsers = usersOptions?.data || []
        const selectedRegions = filters['region'] || []
        const selectedAreas = filters['area'] || []
        return allUsers
            .filter((user: any) => {
                const matchRegion =
                    selectedRegions.length === 0 || (user.region && selectedRegions.includes(user.region.id))
                const matchArea =
                    selectedAreas.length === 0 ||
                    (user.areas && user.areas.some((a: any) => selectedAreas.includes(a.id)))
                return matchRegion && matchArea
            })
            .map((u: any) => ({ id: u.id, name: u.fullName }))
    }, [usersOptions, filters.region, filters.area])

    const selectedRegions = useMemo(() => filters['region'] || [], [filters.region])
    const selectedAreas = useMemo(() => filters['area'] || [], [filters.area])
    const selectedProjects = useMemo(() => filters['project'] || [], [filters.project])
    const selectedUsers = useMemo(() => filters['responsibleUser'] || [], [filters.responsibleUser])

    // Auto-prune area, project and manager filters when region/area selection changes
    useEffect(() => {
        if (!areasData?.data || !projectsData?.data || !usersOptions?.data) return

        let newAreaFilters = selectedAreas
        let newProjectFilters = selectedProjects
        let newUserFilters = selectedUsers
        let hasChanges = false

        // 1. Prune Areas if Region is empty
        if (selectedRegions.length === 0) {
            if (selectedAreas.length > 0) {
                newAreaFilters = []
                hasChanges = true
            }
        } else {
            // Prune Areas that are not in selected Regions
            const validAreaIds = new Set(
                areasData.data.filter((a: any) => selectedRegions.includes(a.region?.id)).map((a: any) => a.id),
            )
            const pruned = selectedAreas.filter(id => validAreaIds.has(id))
            if (pruned.length !== selectedAreas.length) {
                newAreaFilters = pruned
                hasChanges = true
            }
        }

        // 2. Prune Projects not matching selected Region/Area
        if (selectedRegions.length > 0 || selectedAreas.length > 0) {
            const validProjectIds = new Set(
                projectsData.data
                    .filter((p: any) => {
                        const matchRegion =
                            selectedRegions.length === 0 || (p.region && selectedRegions.includes(p.region.id))
                        const matchArea = selectedAreas.length === 0 || (p.area && selectedAreas.includes(p.area.id))
                        return matchRegion && matchArea
                    })
                    .map((p: any) => p.id),
            )
            const pruned = selectedProjects.filter(id => validProjectIds.has(id))
            if (pruned.length !== selectedProjects.length) {
                newProjectFilters = pruned
                hasChanges = true
            }
        }

        // 3. Prune Responsible Users not matching selected Region/Area
        if (selectedRegions.length > 0 || selectedAreas.length > 0) {
            const validUserIds = new Set(
                usersOptions.data
                    .filter((user: any) => {
                        const matchRegion =
                            selectedRegions.length === 0 || (user.region && selectedRegions.includes(user.region.id))
                        const matchArea =
                            selectedAreas.length === 0 ||
                            (user.areas && user.areas.some((a: any) => selectedAreas.includes(a.id)))
                        return matchRegion && matchArea
                    })
                    .map((user: any) => user.id),
            )
            const pruned = selectedUsers.filter(id => validUserIds.has(id))
            if (pruned.length !== selectedUsers.length) {
                newUserFilters = pruned
                hasChanges = true
            }
        }

        if (hasChanges) {
            setFilters(prev => {
                const updated = {
                    ...prev,
                    area: newAreaFilters,
                    project: newProjectFilters,
                    responsibleUser: newUserFilters,
                }
                onFilterChange(updated)
                return updated
            })
        }
    }, [
        selectedRegions,
        selectedAreas,
        selectedProjects,
        selectedUsers,
        areasData,
        projectsData,
        usersOptions,
        onFilterChange,
    ])

    const handleCategoryChange = (key: string, values: string[]) => {
        const newFilters = { ...filters, [key]: values }
        setFilters(newFilters)
        onFilterChange(newFilters)
    }

    const handleDateRangeChange = (value: BsDateRangePickerValue | undefined) => {
        setDateRange(value)
    }

    const quickOptions = useDateRangeQuickOptions()

    return (
        <div className="pb-4 border-b border-border space-y-4">
            <h3 className="text-sm font-semibold text-text-heading">
                {t('common.searchFilter', { defaultValue: 'Bộ lọc tìm kiếm' })}
            </h3>
            <div className={`flex flex-wrap gap-2 ${className}`}>
                <FilterDropdown
                    placeholder={t('projects.list.filters.regionPlaceholder')}
                    inputPlaceholder="projects.list.filters.regionPlaceholder"
                    options={regionOpts}
                    selectedValues={filters['region'] || []}
                    onChange={v => handleCategoryChange('region', v)}
                    isLoading={isRegionsLoading}
                />
                <FilterDropdown
                    placeholder={t('projects.list.filters.areaPlaceholder')}
                    inputPlaceholder="projects.list.filters.areaPlaceholder"
                    options={areaOpts}
                    selectedValues={filters['area'] || []}
                    onChange={v => handleCategoryChange('area', v)}
                    isLoading={isAreasLoading}
                    isDisabled={selectedRegions.length === 0}
                    disabledTooltip={t('common.filters.requireRegionFirst')}
                />

                <FilterDropdown
                    placeholder={t('users.labels.project')}
                    inputPlaceholder="users.labels.project"
                    options={projectOpts}
                    selectedValues={filters['project'] || []}
                    onChange={v => handleCategoryChange('project', v)}
                    isLoading={isProjectsLoading}
                />

                <FilterDropdown
                    placeholder={t('projects.addDocument.documentType')}
                    inputPlaceholder={t('projects.addDocument.documentTypePlaceholder')}
                    options={docTypeOpts}
                    selectedValues={filters['documentType'] || []}
                    onChange={v => handleCategoryChange('documentType', v)}
                    isLoading={isDocumentTypeLoading}
                />

                <FilterDropdown
                    placeholder={t('projects.list.filters.statusPlaceholder')}
                    inputPlaceholder={t('projects.list.filters.statusPlaceholder')}
                    options={statusOptions}
                    selectedValues={filters['status'] || []}
                    onChange={v => handleCategoryChange('status', v)}
                    containerClassName="max-lg:flex-1"
                    className="max-lg:w-full"
                />

                <FilterDropdown
                    placeholder={t('projects.list.filters.managerPlaceholder')}
                    inputPlaceholder="projects.list.filters.managerPlaceholder"
                    options={userOpts}
                    selectedValues={filters['responsibleUser'] || []}
                    onChange={v => handleCategoryChange('responsibleUser', v)}
                    isLoading={isUsersLoading}
                />

                <BsDateRangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    visibleMonths={1}
                    quickOptions={quickOptions}
                    className="w-fit [&_input]:text-sm [&_input]:text-foreground [&_span]:text-sm [&_span]:text-foreground max-lg:w-full"
                    placeholder={t('globalSearch.timePlaceholder')}
                />
            </div>
        </div>
    )
}
